// Client for the Location Support "xcube-status" endpoint. This is a separate service from
// x-cube-api (its own host, like the Contacts BFF): it reports the fleet-scan status of a
// location's X-Cubes so the Hub can show a green/orange/red stoplight and the agent version.
// The Hub only renders what the endpoint returns; the scan logic lives in Location Support.

function statusHost() {
    const raw = import.meta.env.VITE_APP_XCUBE_STATUS_API_URL || "https://control.hub.x-cube.nl";
    return String(raw).replace(/\/$/, "");
}

function getToken() {
    return localStorage.getItem("xcube-session-key");
}

export class XcubeStatusApiError extends Error {
    constructor(message, status, body) {
        super(message);
        this.name = "XcubeStatusApiError";
        this.status = status;
        this.body = body;
    }
}

/**
 * Fetch X-Cube status for one location.
 * @param {number|string} locationId Strapi location id. Without it the service returns the
 *   cubes of all the user's locations; a foreign location returns `cubes: []` with HTTP 200.
 * @returns {Promise<{ ok: boolean, scannedAt: string|null, latestAgentVersion: string,
 *   cubes: Array<{ xcubeId:number, code:string, locationId:number, locationName:string,
 *     status:"ok"|"attention"|"critical", watched:boolean, snoozeUntil:string|null,
 *     issues:Array<{ id, severity:"critical"|"attention"|"minor", label:string }>,
 *     agents:Array<{ role:"main"|"nuc"|"kiosk", version:string|null, upToDate:boolean,
 *       lastSeen:string|null, stale:boolean }> }> }>}
 */
export async function getXcubeStatus(locationId = null) {
    const token = getToken();
    const params = new URLSearchParams();
    if (locationId != null && locationId !== "all") {
        params.set("location_id", String(locationId));
    }
    const query = params.toString();
    const res = await fetch(
        `${statusHost()}/api/hub/xcube-status${query ? `?${query}` : ""}`,
        {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
    );

    let json = {};
    try {
        json = await res.json();
    } catch {
        // Empty or non-JSON bodies still carry a usable HTTP status.
        json = {};
    }

    if (res.status === 401) {
        // Same cross-service caveat as Contacts: a valid Hub JWT can be rejected by Location
        // Support (e.g. testhub JWT against a prod service). Do not wipe the Hub session.
        throw new XcubeStatusApiError(
            json.error || "X-Cube status session rejected by the service",
            401,
            json,
        );
    }

    if (!res.ok || json.ok === false) {
        throw new XcubeStatusApiError(json.error || "Request failed", res.status, json);
    }

    return json;
}
